import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from '@/components/ui/Spinner';
import { ThemeToggle } from '@/core/oblique-ui';

const PLANS = [
    { id: 'starter', name: 'Starter', price: '$49', period: '/mo', desc: 'Perfect for small teams.', modules: ['crm', 'quality'] },
    { id: 'pro', name: 'Professional', price: '$99', period: '/mo', desc: 'Advanced analytics & more seats.', modules: ['crm', 'quality', 'lms', 'analytics'] },
    { id: 'enterprise', name: 'Enterprise OS', price: '$299', period: '/mo', desc: 'Full suite of tools and unlimited scalability.', modules: ['crm', 'quality', 'lms', 'analytics', 'automation', 'developer', 'ai'] }
];

export default function OrganizationOnboarding() {
    const navigate = useNavigate();
    const { createCompany, companies, loading: companiesLoading } = useCompany();
    const { user, userData, loading: authLoading } = useAuth();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Guard: Existing users must NEVER see the onboarding page (feels like a glitch)
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/login', { replace: true });
            return;
        }

        const storedLastPath = localStorage.getItem('oblique_last_path');
        const remoteLastPath = userData?.lastVisitedPath;
        const candidatePath = storedLastPath || remoteLastPath;
        const destination = (candidatePath && candidatePath !== '/onboarding' && candidatePath !== '/hub' && candidatePath !== '/login')
            ? candidatePath
            : '/crm';

        const isExistingUser = 
            localStorage.getItem('oblique_onboarding_completed') === 'true' ||
            userData?.onboardingCompleted === true ||
            userData?.role === 'superadmin' ||
            userData?.role === 'admin' ||
            userData?.role === 'manager' ||
            (companies && companies.length > 0) ||
            !!storedLastPath ||
            !!remoteLastPath;

        if (isExistingUser) {
            navigate(destination, { replace: true });
        }
    }, [user, userData, companies, authLoading, navigate]);

    // Form State
    const [orgName, setOrgName] = useState('');
    const [orgIndustry, setOrgIndustry] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('pro');

    const handleCreateOrganization = async () => {
        if (!orgName) return;
        setLoading(true);
        try {
            const planDetails = PLANS.find(p => p.id === selectedPlan);
            const newComp = await createCompany({
                name: orgName,
                industry: orgIndustry,
                type: 'holding',
                plan: selectedPlan,
                active_modules: planDetails.modules,
                seats: planDetails.id === 'starter' ? 3 : (planDetails.id === 'pro' ? 10 : 50)
            });

            localStorage.setItem('oblique_company_id', newComp.id);
            localStorage.setItem('oblique_onboarding_completed', 'true');
            localStorage.setItem('oblique_last_path', '/crm');

            if (user?.email) {
                const emailLower = user.email.toLowerCase();
                try {
                    await setDoc(doc(db, 'app_users', emailLower), {
                        uid: emailLower,
                        email: emailLower,
                        onboardingCompleted: true,
                        companyId: newComp.id,
                        lastCompanyId: newComp.id,
                        lastVisitedPath: '/crm',
                        updatedAt: serverTimestamp()
                    }, { merge: true });
                } catch (e) {
                    console.error("Failed to update user profile with onboarding info:", e);
                }
            }

            navigate('/crm', { replace: true });
        } catch (error) {
            console.error("Failed to create organization:", error);
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            minHeight: '100vh', width: '100vw', background: 'var(--bg-page)', 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            padding: '40px 20px', position: 'relative', overflow: 'hidden' 
        }}>
            {/* Background 3D Blobs */}
            <div style={{ position: 'absolute', top: '-10%', left: '-5%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(139,92,246,0) 70%)', borderRadius: '50%', filter: 'blur(40px)', zIndex: 0 }} />
            <div style={{ position: 'absolute', bottom: '-10%', right: '-5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(59,130,246,0) 70%)', borderRadius: '50%', filter: 'blur(40px)', zIndex: 0 }} />

            <ThemeToggle style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 10 }} floating={false} />
            
            <motion.div 
                initial={{ opacity: 0, y: 30, rotateX: 10 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                style={{ 
                    maxWidth: '900px', width: '100%', display: 'flex', background: 'var(--bg-surface)', 
                    border: '1px solid var(--border)', borderRadius: '24px', 
                    boxShadow: '0 32px 64px -12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.1)', 
                    overflow: 'hidden', zIndex: 1, perspective: '1000px',
                    flexDirection: window.innerWidth < 768 ? 'column' : 'row'
                }}
            >
                {/* Illustration Panel */}
                <div style={{ flex: 1, background: 'linear-gradient(135deg, var(--accent) 0%, #3b82f6 100%)', padding: '48px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1, backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)', backgroundSize: '40px 40px' }} />
                    <motion.div 
                        animate={{ y: [0, -10, 0] }}
                        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                        style={{ width: '80px', height: '80px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}
                    >
                        <ObliqueIcon name={step === 1 ? "rocket_launch" : "diamond"} size={40} color="white" />
                    </motion.div>
                    <h2 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '16px', letterSpacing: '-0.02em', zIndex: 1 }}>
                        {step === 1 ? 'Launch your next big thing.' : 'Unlock your team\'s potential.'}
                    </h2>
                    <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.8)', lineHeight: '1.6', zIndex: 1 }}>
                        {step === 1 
                            ? 'Set up your workspace in seconds and start collaborating with your team on a unified operating system.'
                            : 'Choose a plan that scales with you. No hidden fees, just pure productivity and growth.'}
                    </p>
                    
                    {/* Floating elements for 3D effect */}
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 50, ease: "linear" }} style={{ position: 'absolute', top: '10%', right: '10%', opacity: 0.2 }}>
                        <ObliqueIcon name="star" size={60} color="white" />
                    </motion.div>
                    <motion.div animate={{ rotate: -360 }} transition={{ repeat: Infinity, duration: 40, ease: "linear" }} style={{ position: 'absolute', bottom: '10%', left: '10%', opacity: 0.2 }}>
                        <ObliqueIcon name="hex" size={80} color="white" />
                    </motion.div>
                </div>

                {/* Form Panel */}
                <div style={{ flex: 1, padding: '48px', background: 'var(--bg-surface)' }}>
                    <div style={{ textAlign: 'left', marginBottom: '40px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Step {step} of 2</div>
                        </div>
                        <h1 style={{ fontSize: '28px', fontWeight: '600', color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: '8px' }}>
                            {step === 1 ? 'Create Workspace' : 'Select a Plan'}
                        </h1>
                    </div>

                    <AnimatePresence mode="wait">
                        {step === 1 && (
                            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <div style={{ position: 'relative' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '8px' }}>Organization Name</label>
                                        <div style={{ position: 'absolute', top: '40px', left: '16px', color: 'var(--text-tertiary)' }}><ObliqueIcon name="business" size={20} /></div>
                                        <input 
                                            type="text" 
                                            className="input" 
                                            placeholder="e.g. Acme Corporation" 
                                            value={orgName} 
                                            onChange={(e) => setOrgName(e.target.value)} 
                                            style={{ width: '100%', height: '52px', fontSize: '15px', padding: '0 16px 0 48px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-muted)', color: 'var(--text-primary)', transition: 'all 0.2s', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}
                                            onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                                            onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                                        />
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '8px' }}>Industry</label>
                                        <div style={{ position: 'absolute', top: '40px', left: '16px', color: 'var(--text-tertiary)' }}><ObliqueIcon name="category" size={20} /></div>
                                        <input 
                                            type="text" 
                                            className="input" 
                                            placeholder="e.g. Technology, Healthcare, Sales" 
                                            value={orgIndustry} 
                                            onChange={(e) => setOrgIndustry(e.target.value)} 
                                            style={{ width: '100%', height: '52px', fontSize: '15px', padding: '0 16px 0 48px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-muted)', color: 'var(--text-primary)', transition: 'all 0.2s', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)' }}
                                            onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                                            onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                                        />
                                    </div>
                                </div>
                                <motion.button 
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setStep(2)} 
                                    disabled={!orgName}
                                    className="btn btn-primary" 
                                    style={{ width: '100%', height: '52px', marginTop: '40px', fontSize: '16px', fontWeight: '600', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', opacity: !orgName ? 0.5 : 1, boxShadow: '0 8px 16px rgba(139, 92, 246, 0.2)' }}
                                >
                                    Continue <ObliqueIcon name="chevron_right" size={20} style={{ marginLeft: '8px' }} />
                                </motion.button>
                            </motion.div>
                        )}

                        {step === 2 && (
                            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {PLANS.map(plan => (
                                        <motion.div 
                                            whileHover={{ y: -2 }}
                                            key={plan.id}
                                            onClick={() => setSelectedPlan(plan.id)}
                                            style={{ 
                                                padding: '24px', 
                                                borderRadius: '16px', 
                                                border: `2px solid ${selectedPlan === plan.id ? 'var(--accent)' : 'var(--border)'}`,
                                                background: selectedPlan === plan.id ? 'var(--accent-subtle)' : 'var(--bg-surface)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                boxShadow: selectedPlan === plan.id ? '0 12px 24px rgba(139, 92, 246, 0.15)' : 'none',
                                                transform: selectedPlan === plan.id ? 'scale(1.02)' : 'scale(1)'
                                            }}
                                        >
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                    {selectedPlan === plan.id && <ObliqueIcon name="check_circle" size={18} color="var(--accent)" />}
                                                    <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{plan.name}</div>
                                                </div>
                                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', paddingLeft: selectedPlan === plan.id ? '26px' : '0' }}>{plan.desc}</div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>{plan.price}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{plan.period}</div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
                                    <button 
                                        onClick={() => setStep(1)} 
                                        className="btn btn-secondary" 
                                        style={{ width: '120px', height: '52px', fontSize: '15px', fontWeight: '600', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer' }}
                                    >
                                        Back
                                    </button>
                                    <motion.button 
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleCreateOrganization} 
                                        disabled={loading}
                                        className="btn btn-primary" 
                                        style={{ flex: 1, height: '52px', fontSize: '16px', fontWeight: '600', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer', opacity: loading ? 0.7 : 1, boxShadow: '0 8px 16px rgba(139, 92, 246, 0.2)' }}
                                    >
                                        {loading ? <Spinner size={20} color="white" /> : 'Complete Setup'}
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
}
